export interface CountdownData {
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
  total: number;
}

/**
 * Countdown composable that can accept a direct date string or a computed ref
 * @param targetDate ISO date string or Date object (can be a Ref or ComputedRef)
 */
export function useCountdown(targetDate: MaybeRef<string | Date>) {
  // Initialize with zeros for SSR
  const countdownData = reactive<CountdownData>({
    days: "00",
    hours: "00",
    minutes: "00",
    seconds: "00",
    total: 0,
  });

  const isUrgent = ref(false);
  const isExpired = ref(false);
  const timer = ref<number | null>(null);

  // Function to calculate and update the countdown
  const updateCountdown = () => {
    if (typeof window === "undefined") return;

    const now = new Date();
    const deadlineValue = unref(targetDate);
    const deadline = new Date(deadlineValue);

    // Check if the date is valid
    if (isNaN(deadline.getTime())) {
      console.warn("Invalid date provided to useCountdown:", deadlineValue);
      return;
    }

    const diff = deadline.getTime() - now.getTime();

    if (diff <= 0) {
      // Deadline has passed
      countdownData.days = "00";
      countdownData.hours = "00";
      countdownData.minutes = "00";
      countdownData.seconds = "00";
      countdownData.total = 0;
      isExpired.value = true;

      // Clear the timer
      if (timer.value !== null) {
        window.clearInterval(timer.value);
        timer.value = null;
      }
      return;
    }

    // Calculate time units
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    // Update reactive data
    countdownData.days = days.toString().padStart(2, "0");
    countdownData.hours = hours.toString().padStart(2, "0");
    countdownData.minutes = minutes.toString().padStart(2, "0");
    countdownData.seconds = seconds.toString().padStart(2, "0");
    countdownData.total = diff;

    // Check if urgent (less than 24 hours)
    isUrgent.value = diff <= 24 * 60 * 60 * 1000;
  };

  // Watch for changes to the target date (if it's a ref or computed)
  watch(
    () => unref(targetDate),
    () => {
      updateCountdown();
    },
  );

  onMounted(() => {
    // Run immediately once
    updateCountdown();

    // Then set interval (only on client-side)
    if (typeof window !== "undefined") {
      timer.value = window.setInterval(updateCountdown, 1000);
    }
  });

  onUnmounted(() => {
    // Clean up the timer
    if (timer.value !== null && typeof window !== "undefined") {
      window.clearInterval(timer.value);
      timer.value = null;
    }
  });

  return {
    countdownData,
    isUrgent,
    isExpired,
  };
}
